/**
 * Blanket Fit Finder wizard — vanilla JS, framework-free.
 *
 * Companion to fly-fit-finder.js. Mounts inside a Shopify theme section
 * (Online Store 2.0) but works anywhere with a root element. Public API:
 *
 *   BlanketFitFinder.mount(rootEl, {
 *     rulesUrl,               // required
 *     productMapUrl,          // optional
 *     imageUrls,              // optional { basename: url }
 *     productUrlBase,         // optional
 *     fallbackSearchUrl,      // optional
 *     collectionUrl,          // optional
 *     fitSpecialistUrl,       // optional
 *     measuringGuideUrl,      // optional
 *     klaviyoPublicKey,       // optional
 *     klaviyoListId,          // optional
 *     shareUrlBase,           // optional canonical URL for share-back links
 *     eqLearningUrl,          // optional Schneiders EQ community CTAs
 *     eqMainUrl,              // optional
 *     customerId,             // optional (auto-detected from Shopify.customer)
 *     onComplete              // optional callback(resolved)
 *   });
 *
 * Analytics events (GA4 + dataLayer):
 *   blanket_finder_start
 *   blanket_finder_step         { step, field, value }
 *   blanket_finder_resume       { step, source }
 *   blanket_finder_no_match     { trace, blanket_horse_type }
 *   blanket_finder_complete     { blanket_horse_type, blanket_size, blanket_fit,
 *                                 blanket_style, blanket_type, blanket_durability,
 *                                 climate, weights, slots[], recommended_products[] }
 *   blanket_finder_outbound_click { slot, product_number, handle, source }
 *   blanket_finder_share_copy   { fields }
 *   blanket_finder_share_open   { fields }
 *   blanket_finder_email_submit { marketing_optin, capture_point }
 *   blanket_finder_step15_skip  { reason }
 *   blanket_finder_abandon      { last_step_index, last_step_id, answered }
 *
 * Klaviyo metrics (when klaviyoPublicKey is set):
 *   Blanket Finder Completed
 *   Blanket Finder Email Capture
 *   Blanket Finder Abandoned
 */
(function (root) {
  "use strict";

  var DEFAULTS = {
    productUrlBase: "/products/",
    fallbackSearchUrl: "/search?q=",
    imagesBase: "",
    imageUrls: null,
    collectionUrl: "/collections/horse-blankets",
    fitSpecialistUrl: "",
    measuringGuideUrl: "",
    klaviyoPublicKey: "",
    klaviyoListId: "",
    shareUrlBase: "",
    eqLearningUrl: "",
    eqMainUrl: "",
    storageKey: "blanketFitFinder",
    storageVersion: 1,
    customerId: null,
    enablePersistence: true,
    enableAbandonTracking: true,
    enableShareLinks: true,
    enableHorseProfiles: true,
    enableEmailCapture: true
  };

  function imageUrl(opts, basename) {
    if (!basename) return null;
    if (opts && opts.imageUrls && opts.imageUrls[basename]) return opts.imageUrls[basename];
    if (opts && opts.imagesBase) return opts.imagesBase + basename;
    return null;
  }

  // --------------------------------------------------------------------------
  // Step definitions — 14 wizard questions + optional email-capture final step
  // when Klaviyo is configured. Vocabulary sourced from Kalio's
  // BlanketFinderAnswerValues (2020 admin export).
  // --------------------------------------------------------------------------
  var STEPS = [
    {
      id: "horse_name", type: "text", optional: true,
      label: "What is your horse's name?",
      help: "We'll use this to personalize your recommendation. Optional.",
      placeholder: "e.g. Fluffy"
    },
    {
      id: "zip", type: "zip",
      label: "What is your zip code?",
      help: "We use this to identify your climate — cold vs hot — which drives blanket weight and turnout recommendations.",
      placeholder: "e.g. 44087"
    },
    {
      id: "horse_type", type: "image-radio",
      label: "Is your horse a...",
      options: [
        { value: "Mini",  label: "Mini",  image: "Mini.jpg" },
        { value: "Pony",  label: "Pony",  image: "Pony.jpg" },
        { value: "Horse", label: "Horse", image: "Horse.jpg" },
        { value: "Draft", label: "Draft", image: "Draft.jpg" }
      ]
    },
    {
      id: "breed", type: "select",
      label: "What breed is your horse?",
      help: "Pick the closest match — this refines the size lookup."
    },
    {
      id: "height", type: "select", dynamic: "height_options",
      label: "How tall is your horse?",
      help: "Ponies, Horses and Drafts in hands; Minis in inches."
    },
    {
      id: "chest", type: "image-radio",
      label: "What best describes your horse's chest?",
      measure: "Measure from one point of shoulder, across the chest, to the other point of shoulder. Under ~14\" narrow, ~14-18\" standard, over ~18\" wide.",
      options: [
        { value: "Narrow",   label: "Narrow",   image: "Narrow Chest Width.jpg" },
        { value: "Standard", label: "Standard", image: "Medium Chest Width.jpg" },
        { value: "Wide",     label: "Wide",     image: "WIDE Chest Width.jpg" }
      ]
    },
    {
      id: "withers_and_head_carriage", type: "image-radio",
      label: "Which best describes your horse's withers and head carriage?",
      help: "High, well-defined withers typically point us toward our VTEK® or V-Free® fits. Low to flat withers open up Cutback and Euro options.",
      measure: "High withers sit sharply above the topline; low withers blend smoothly into the neck. Head carriage is where the horse naturally holds their head at rest — low (relaxed) or high (upright).",
      options: [
        { value: "Low/Low",   label: "Low withers, low head carriage",   image: "LowW-LowHC.jpg" },
        { value: "Low/High",  label: "Low withers, high head carriage",  image: "LowW-HighHC.jpg" },
        { value: "High/Low",  label: "High withers, low head carriage",  image: "HighW-LowHC.jpg" },
        { value: "High/High", label: "High withers, high head carriage", image: "HighW-HighHC.jpg" }
      ]
    },
    {
      id: "shoulders", type: "image-radio",
      label: "Which best describes your horse's shoulders?",
      help: "Sloped shoulders often route to our V-Free®, Cutback, or Euro fits. Upright shoulders line up with VTEK® or Euro.",
      measure: "Sloped shoulders angle back sharply from the withers; upright shoulders are more vertical.",
      options: [
        { value: "Sloped",  label: "Sloped",  image: "Sloped-Shoulders.jpg" },
        { value: "Upright", label: "Upright", image: "Upright-Shoulders.jpg" }
      ]
    },
    {
      id: "mane", type: "image-radio",
      label: "How is your horse's mane kept?",
      help: "Long, natural manes are often best matched with a Cutback fit that leaves the mane untouched.",
      options: [
        { value: "Long",  label: "Long / natural", image: "Long Mane.jpg" },
        { value: "Short", label: "Pulled / short", image: "Pulled Mane Sam.jpg" }
      ]
    },
    {
      id: "horse_age", type: "number",
      label: "How old is your horse?",
      help: "We use age to decide whether a senior fit adjustment applies.",
      min: 0, max: 31, placeholder: "e.g. 8"
    },
    {
      id: "habitat", type: "radio",
      label: "Where does your horse live?",
      options: [
        { value: "24-7 Turnout with Run-in shed", label: "24/7 turnout with a run-in shed" },
        { value: "Stall/Fair weather",             label: "Stalled — turned out only in fair weather" },
        { value: "Stall/Turnout in all weather",   label: "Stalled — turned out in all weather" }
      ]
    },
    {
      id: "temperament", type: "image-radio",
      label: "What is your horse's temperament and buddy situation?",
      options: [
        { value: "Calm/Alone",   label: "Calm, lives alone",             image: "CalmLivesALone_Activity.jpg" },
        { value: "Calm/Calm",    label: "Calm with calm buddies",        image: "CalmCalmBuddies_Activity.jpg" },
        { value: "Calm/Rough",   label: "Calm with rowdy buddies",       image: "CalmRoughBuddies_Activity.jpg" },
        { value: "Rough/Calm",   label: "Rowdy with calm buddies",       image: "RoughtCalmBuddies_Activity.jpg" },
        { value: "Rough/Rough",  label: "Rowdy with rowdy buddies",      image: "RoughRoughBuddies_Activity.jpg" }
      ]
    },
    {
      id: "coat", type: "image-radio",
      label: "What is your horse's coat like this season?",
      options: [
        { value: "Body-clipped, show-ready",                   label: "Body-clipped, show-ready",             image: "Body Clipped Sam.jpg" },
        { value: "Trace Clipped",                              label: "Trace clipped",                        image: "Trace Clipped Sam.jpg" },
        { value: "Natural unclipped coat, but thin or short",  label: "Natural unclipped, thin or short coat", image: "Natural Coat Short or Thin Sam.jpg" },
        { value: "Long, wooly, natural coat",                  label: "Long, wooly, natural coat",            image: "Natural Coat Thick or Fluffy.jpg" }
      ]
    },
    {
      id: "body_condition", type: "image-radio",
      label: "What is your horse's body condition?",
      options: [
        { value: "Fat, easy keeper",                         label: "Fat / easy keeper",   image: "Fat_Sam.jpg" },
        { value: "Average weight",                           label: "Average",             image: "Average_Sam.jpg" },
        { value: "Old, thin or hard to keep weight on",      label: "Thin / hard keeper",  image: "Thin_Sam.jpg" }
      ]
    },
    // Optional soft email gate — only rendered when Klaviyo public key is set
    // AND enableEmailCapture is true. See renderEmailCaptureStep.
    {
      id: "email_capture", type: "email-capture", optional: true,
      label: "Want a copy of your recommendations?",
      help: "We'll email you a personalized summary with the products we recommend, your horse's profile, and a link you can revisit anytime."
    }
  ];

  var SLOT_LABELS = {
    stable_spring: "Stable / Spring",
    stable_winter: "Stable / Winter",
    turnout_spring: "Turnout / Spring",
    turnout_winter: "Turnout / Winter"
  };

  var SLOT_TO_WEIGHT_COL = {
    stable_spring: "blanket_weight_stable_spring",
    stable_winter: "blanket_weight_stable_winter",
    turnout_spring: "blanket_weight_turnout_spring",
    turnout_winter: "blanket_weight_turnout_winter"
  };

  var RESULT_SLOTS = [
    { key: "stable_spring",  title: "Stable / Spring",  primary: false },
    { key: "stable_winter",  title: "Stable / Winter",  primary: true  },
    { key: "turnout_spring", title: "Turnout / Spring", primary: false },
    { key: "turnout_winter", title: "Turnout / Winter", primary: true  }
  ];

  // Narrative copy map — humanized forms of Kalio option values, used in the
  // result summary and Klaviyo payloads.
  // --------------------------------------------------------------------------
  // Fit copy — canonical Schneiders language per merch's fit-talking-points
  // guide (2026-08-20). Keys are the resolver's blanket_fit values.
  //
  // CONSTRAINT: everything here lands in the Klaviyo event payload, and
  // per merch, wither-fit copy must NEVER reference specific breeds. The
  // source guide's Cutback "may lean more toward a stock breed build"
  // line is deliberately omitted; the shared Adjusta-Fit "warmbloods /
  // thoroughbreds / Arabians" paragraph is not carried here either.
  // --------------------------------------------------------------------------
  var FIT_COPY = {
    "VTEK": {
      short: "VTEK®",
      lead: "Designed with our VTEK® Wither Relief fit, this blanket is well suited for horses with an upright shoulder, a high, very well-defined wither of moderate length, a balanced topline from wither to croup, strong muscling, and heavier bone structure.",
      bullets: [
        "Upright shoulder structure",
        "High, very well-defined wither",
        "Moderate wither length",
        "Balanced topline from wither to croup",
        "Strong muscling and heavier bone structure",
        "VTEK® wither extends in front of the wither",
        "Helps relieve pressure points on high-withered horses"
      ]
    },
    "V-Free": {
      short: "V-Free®",
      lead: "Designed with our V-Free® Wither Relief fit, this blanket is well suited for horses with a naturally sloped shoulder, moderately defined shorter wither, and strong, well-muscled topline.",
      bullets: [
        "Naturally sloped shoulder",
        "Moderately defined shorter wither",
        "Strong topline from wither to croup",
        "Shorter coupled and well muscled",
        "V-Free® wither covers the wither and ends where the mane starts",
        "Helps protect the wither from rubs and pressure points"
      ]
    },
    "Cutback": {
      short: "Cutback",
      lead: "Designed with our Cutback fit, this blanket is well suited for horses with a naturally sloped shoulder, low to flat shorter wither, nicely toned muscle, and flatter topline.",
      bullets: [
        "Naturally sloped shoulder and balanced frame",
        "Low to flat, shorter wither",
        "Nicely toned muscle and flatter topline",
        "Often suited to upright head carriage",
        "Often suited to longer manes",
        "Cutback wither leaves the mane untouched"
      ]
    },
    "Euro": {
      short: "Euro",
      lead: "Designed with a Euro fit, this blanket accommodates both sloped and upright shoulder conformations and is well suited for horses with a low to flat shorter wither, balanced muscle tone, and smoother, flatter topline appearance.",
      bullets: [
        "Versatile fit",
        "Accommodates sloped or upright shoulder conformations",
        "Low to flat, shorter wither",
        "Balanced muscle tone",
        "Smoother, flatter topline appearance",
        "Suitable for slightly downhill horses"
      ]
    }
  };

  function fitCopy(fitValue) {
    if (!fitValue) return null;
    return FIT_COPY[fitValue] || FIT_COPY[String(fitValue).replace(/-free/i, "-Free")] || null;
  }

  var NARRATIVE = {
    horse_type: { "Mini": "Mini", "Pony": "Pony", "Horse": "Horse", "Draft": "Draft" },
    breed: {
      "Stock": "Stock Horse breed", "Warmblood": "Warmblood", "Thoroughbred": "Thoroughbred",
      "Arabian": "Arabian", "Pony": "Pony", "Mini": "Mini", "Draft": "Draft",
      "Hanovarian": "Hanoverian" // preserve Kalio's spelling in source, correct in output
    },
    chest: { "Wide": "wide", "Standard": "standard", "Narrow": "narrow" },
    mane: { "Long": "long mane", "Short": "short mane" },
    shoulders: { "Sloped": "sloped shoulders", "Upright": "upright shoulders" },
    withers_and_head_carriage: {
      "Low/Low":   "low withers, low head carriage",
      "High/Low":  "high withers, low head carriage",
      "Low/High":  "low withers, high head carriage",
      "High/High": "high withers, high head carriage"
    },
    temperament: {
      "Calm/Alone":  "calm on their own",
      "Calm/Calm":   "calm with calm buddies",
      "Calm/Rough":  "calm with rowdy buddies",
      "Rough/Calm":  "rowdy with calm buddies",
      "Rough/Rough": "rowdy with rowdy buddies"
    },
    habitat: {
      "24-7 Turnout with Run-in shed": "living out 24/7 with a shed",
      "Stall/Fair weather": "stalled and turned out only in fair weather",
      "Stall/Turnout in all weather": "stalled and turned out in all weather"
    },
    coat: {
      "Body-clipped, show-ready": "body-clipped",
      "Trace Clipped": "trace-clipped",
      "Natural unclipped coat, but thin or short": "unclipped with a thin coat",
      "Long, wooly, natural coat": "wearing a full winter coat"
    },
    body_condition: {
      "Fat, easy keeper": "an easy keeper",
      "Average weight": "at an average weight",
      "Old, thin or hard to keep weight on": "hard to keep weight on"
    },
    climate: { "Cold climate": "cold", "Hot climate": "hot", "Moderate climate": "moderate" }
  };

  function narrate(field, value) {
    if (value == null || value === "") return "";
    if (NARRATIVE[field] && NARRATIVE[field][value]) return NARRATIVE[field][value];
    return String(value);
  }

  function ageBucket(age) {
    var n = parseInt(age, 10);
    if (isNaN(n)) return "Not Old";
    return n >= 15 ? "Old" : "Not Old";
  }

  function ageNarrative(value) {
    if (value === "" || value == null) return "";
    var n = parseInt(value, 10);
    if (isNaN(n)) return "";
    if (n === 0) return "under 1 year old";
    if (n === 1) return "1 year old";
    if (n === 31) return "over 30 years old";
    return n + " years old";
  }

  function normalizeBreed(value) {
    if (!value) return value;
    return String(value).replace(/\s*Breed$/i, "").replace(/-Type$/i, "").trim();
  }

  function buildHorseSummary(answers, derived) {
    if (!answers) return "";
    var name = answers.horse_name && String(answers.horse_name).trim();
    if (!name) name = "This horse";
    var ageRaw = parseInt(answers.horse_age, 10);
    var ageClause = "";
    if (!isNaN(ageRaw)) {
      // "an" before vowel-sound numbers (8, 11, 18). Wizard caps at 30 so
      // these are the only in-range values that need it.
      var art = (ageRaw === 8 || ageRaw === 11 || ageRaw === 18) ? "an " : "a ";
      if (ageRaw === 0) ageClause = "a foal";
      else if (ageRaw === 31) ageClause = "a senior";
      else if (ageRaw === 1) ageClause = "a 1 year old";
      else if (ageRaw >= 2 && ageRaw <= 30) ageClause = art + ageRaw + " year old";
    }
    var breedWord = narrate("breed", answers.breed) || narrate("horse_type", answers.horse_type) || "horse";
    breedWord = String(breedWord).replace(/\s+breed$/i, "").trim();
    var stateWord = (derived && derived.state) ? derived.state : "your area";
    var temperWord = narrate("temperament", answers.temperament);
    var pieces = [name, "is"];
    if (ageClause) pieces.push(ageClause);
    else pieces.push("a");
    if (breedWord) pieces.push(breedWord);
    pieces.push("who lives in", stateWord);
    // NARRATIVE.temperament values already read as verb phrases
    // ("calm with calm buddies", "rowdy with rowdy buddies"). Prefix with
    // "and is" so the whole sentence flows without the pre-8/20 doubled
    // "with" bug ("who lives in OH with rowdy with calm buddies").
    if (temperWord) pieces.push("and is", temperWord);
    return pieces.join(" ").replace(/\s+/g, " ").trim() + ".";
  }

  function breedTypeWarning(answers) {
    var t = answers.horse_type;
    var b = normalizeBreed(answers.breed);
    var h = answers.height || "";
    var heightHands = parseFloat(h);
    if (!t || !b) return null;
    if (t === "Pony" && b === "Stock" && heightHands >= 13.3) {
      return "Most Stock-breed horses around " + h + " sit in our Horse fit pipeline. If your horse is a Quarter Pony, sticking with Pony is fine — otherwise try Horse for a closer match.";
    }
    if (t === "Pony" && b === "Arabian" && heightHands < 14 && !isNaN(heightHands)) {
      return "Small Arabians can go either way. If the recommendation feels off, try the Horse fit pipeline for the same height.";
    }
    if (t === "Horse" && b === "Warmblood" && heightHands >= 17) {
      return "A 17-hand Warmblood may fit better in our Draft pipeline. If the result feels tight, come back and pick Draft.";
    }
    return null;
  }

  // --------------------------------------------------------------------------
  // Analytics — GA4 + dataLayer, never breaks the wizard
  // --------------------------------------------------------------------------
  function emit(eventName, payload) {
    try {
      if (root.dataLayer && typeof root.dataLayer.push === "function") {
        root.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
      }
      if (typeof root.gtag === "function") {
        root.gtag("event", eventName, payload || {});
      }
    } catch (err) {
      if (root.console) root.console.warn("[BlanketFitFinder] analytics error", err);
    }
  }

  function fetchJSON(url) {
    return fetch(url, { credentials: "same-origin" }).then(function (res) {
      if (!res.ok) throw new Error("Failed to load " + url + " (" + res.status + ")");
      return res.json();
    });
  }

  function expandColumnar(table) {
    if (!table || !table.columns || !table.rows) return [];
    var cols = table.columns;
    return table.rows.map(function (row) {
      var obj = {};
      for (var i = 0; i < cols.length; i++) obj[cols[i]] = row[i];
      return obj;
    });
  }

  function findInTable(rows, predicateFields, query) {
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var hit = true;
      for (var f = 0; f < predicateFields.length; f++) {
        var field = predicateFields[f];
        if (String(row[field]) !== String(query[field])) { hit = false; break; }
      }
      if (hit) return row;
    }
    return null;
  }

  function findClosestSizeRow(rows, breed, height) {
    if (!breed || !height) return null;
    var heightStr = String(height);
    var heightNum = parseFloat(heightStr);
    if (isNaN(heightNum)) return null;
    var heightUnit = heightStr.indexOf("inches") !== -1 ? "inches" : "hands";
    var best = null, bestDist = Infinity;
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (String(row.breed) !== String(breed)) continue;
      var rowStr = String(row.height || "");
      var rowUnit = rowStr.indexOf("inches") !== -1 ? "inches" : "hands";
      if (rowUnit !== heightUnit) continue;
      var rowNum = parseFloat(rowStr);
      if (isNaN(rowNum)) continue;
      var dist = Math.abs(rowNum - heightNum);
      if (dist < bestDist) { bestDist = dist; best = row; }
    }
    return best;
  }

  // --------------------------------------------------------------------------
  // localStorage helpers — versioned + customer-scoped
  // --------------------------------------------------------------------------
  function storageScope(opts) {
    return opts.customerId ? ("c" + opts.customerId) : "anon";
  }
  function sessionKey(opts) {
    return opts.storageKey + ":session:v" + opts.storageVersion + ":" + storageScope(opts);
  }
  function profileKey(opts) {
    return opts.storageKey + ":profiles:v" + opts.storageVersion + ":" + storageScope(opts);
  }
  function safeRead(key) {
    try {
      if (typeof root.localStorage === "undefined") return null;
      var raw = root.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function safeWrite(key, value) {
    try {
      if (typeof root.localStorage === "undefined") return;
      root.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }
  function safeRemove(key) {
    try {
      if (typeof root.localStorage === "undefined") return;
      root.localStorage.removeItem(key);
    } catch (e) {}
  }
  function loadSession(opts) {
    if (!opts.enablePersistence) return null;
    return safeRead(sessionKey(opts));
  }
  function saveSession(opts, state) {
    if (!opts.enablePersistence) return;
    safeWrite(sessionKey(opts), {
      stepIndex: state.stepIndex,
      answers: state.answers,
      savedAt: Date.now()
    });
  }
  function clearSession(opts) { safeRemove(sessionKey(opts)); }
  function loadProfiles(opts) {
    if (!opts.enableHorseProfiles) return [];
    var arr = safeRead(profileKey(opts));
    return Array.isArray(arr) ? arr : [];
  }
  function saveProfile(opts, profile) {
    if (!opts.enableHorseProfiles) return;
    var list = loadProfiles(opts).filter(function (p) { return p.id !== profile.id; });
    list.unshift(profile);
    if (list.length > 10) list = list.slice(0, 10);
    safeWrite(profileKey(opts), list);
  }
  function deleteProfile(opts, id) {
    var list = loadProfiles(opts).filter(function (p) { return p.id !== id; });
    safeWrite(profileKey(opts), list);
  }

  // --------------------------------------------------------------------------
  // Share link encoding — same base64url scheme as Fly Finder, `#bf=<token>`
  // --------------------------------------------------------------------------
  function b64UrlEncode(str) {
    try {
      var b64 = (typeof btoa === "function")
        ? btoa(unescape(encodeURIComponent(str)))
        : Buffer.from(str, "utf8").toString("base64");
      return b64.replace(/=+$/g, "").replace(/\+/g, "-").replace(/\//g, "_");
    } catch (e) { return ""; }
  }
  function b64UrlDecode(str) {
    try {
      var b64 = String(str).replace(/-/g, "+").replace(/_/g, "/");
      while (b64.length % 4) b64 += "=";
      var raw = (typeof atob === "function")
        ? atob(b64)
        : Buffer.from(b64, "base64").toString("utf8");
      return decodeURIComponent(escape(raw));
    } catch (e) { return null; }
  }
  function encodeAnswers(answers) { return b64UrlEncode(JSON.stringify(answers || {})); }
  function decodeAnswers(token) {
    var json = b64UrlDecode(token);
    if (!json) return null;
    try { return JSON.parse(json); } catch (e) { return null; }
  }
  function readShareTokenFromLocation() {
    if (typeof root.location === "undefined") return null;
    var hash = root.location.hash || "";
    var m = hash.match(/[#&]bf=([^&]+)/);
    return m ? m[1] : null;
  }
  function buildShareUrl(opts, answers) {
    if (!opts.enableShareLinks || typeof root.location === "undefined") return null;
    var token = encodeAnswers(answers);
    if (!token) return null;
    var loc = root.location;
    var base = String(opts.shareUrlBase || (loc.origin + loc.pathname + loc.search));
    var hashIdx = base.indexOf("#");
    if (hashIdx !== -1) base = base.substring(0, hashIdx);
    return base + "#bf=" + token;
  }

  // --------------------------------------------------------------------------
  // Klaviyo client — public key only. No-ops when key/klaviyo unset.
  // --------------------------------------------------------------------------
  function klaviyoIdentify(opts, traits) {
    if (!opts.klaviyoPublicKey || !root.klaviyo || typeof root.klaviyo.identify !== "function") return;
    try { root.klaviyo.identify(traits); } catch (e) {}
  }
  function klaviyoTrack(opts, eventName, properties) {
    if (!opts.klaviyoPublicKey || !root.klaviyo || typeof root.klaviyo.track !== "function") return;
    try { root.klaviyo.track(eventName, properties || {}); } catch (e) {}
  }
  function klaviyoSubscribe(opts, email) {
    if (!opts.klaviyoPublicKey || !opts.klaviyoListId || !email) return;
    if (typeof root.fetch !== "function") return;
    var url = "https://a.klaviyo.com/client/subscriptions/?company_id=" +
              encodeURIComponent(opts.klaviyoPublicKey);
    var body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: "Blanket Fit Finder",
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: "list", id: opts.klaviyoListId } }
        }
      }
    };
    try {
      root.fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          "revision": "2024-10-15"
        },
        body: JSON.stringify(body),
        mode: "cors",
        keepalive: true
      });
    } catch (e) {}
  }

  function preloadImages(opts, basenames) {
    if (typeof root.Image === "undefined") return;
    (basenames || []).forEach(function (b) {
      var url = imageUrl(opts, b);
      if (!url) return;
      try { var img = new root.Image(); img.src = url; } catch (e) {}
    });
  }

  // --------------------------------------------------------------------------
  // Resolution algorithm — 6-table cascade
  // --------------------------------------------------------------------------
  function resolve(answers, rules) {
    var trace = [];
    var breed = normalizeBreed(answers.breed);

    // 1. state -> climate
    var state = (root.FFINDER_ZIP_TO_STATE && root.FFINDER_ZIP_TO_STATE.resolve(answers.zip)) || answers.state || null;
    var climateRow = state ? findInTable(rules.climate, ["state"], { state: state }) : null;
    var climate = climateRow ? climateRow.climate : null;
    trace.push({ step: "climate", state: state, value: climate });

    // 2. breed + height -> blanket_size + blanket_horse_type (with closest-height fallback)
    var sizeRow = findInTable(rules.size, ["breed", "height"], { breed: breed, height: answers.height });
    var sizeFallback = false;
    if (!sizeRow) {
      sizeRow = findClosestSizeRow(rules.size, breed, answers.height);
      if (sizeRow) sizeFallback = true;
    }
    var blanket_horse_type = sizeRow ? sizeRow.blanket_horse_type : answers.horse_type;
    var blanket_size = sizeRow ? sizeRow.blanket_size : null;
    trace.push({ step: "size", blanket_horse_type: blanket_horse_type, blanket_size: blanket_size, fallback: sizeFallback });

    // 3. Fit rule -> blanket_fit + blanket_style
    var fitQuery = {
      blanket_horse_type: blanket_horse_type,
      breed: breed,
      withers_and_head_carriage: answers.withers_and_head_carriage,
      shoulders: answers.shoulders,
      chest: answers.chest,
      mane: answers.mane,
      horse_age: ageBucket(answers.horse_age)
    };
    var fitRow = findInTable(rules.fit,
      ["blanket_horse_type", "breed", "withers_and_head_carriage", "shoulders", "chest", "mane", "horse_age"],
      fitQuery);
    // Kalio parity: if strict match misses, drop the age filter.
    if (!fitRow) {
      fitRow = findInTable(rules.fit,
        ["blanket_horse_type", "breed", "withers_and_head_carriage", "shoulders", "chest", "mane"],
        fitQuery);
    }
    var blanket_fit = fitRow ? fitRow.blanket_fit : null;
    var blanket_style = fitRow ? fitRow.blanket_style : null;
    trace.push({ step: "fit", blanket_fit: blanket_fit, blanket_style: blanket_style });

    // 4. Type rule -> blanket_type + blanket_durability
    var typeRow = findInTable(rules.type, ["climate", "habitat", "temperament"], {
      climate: climate,
      habitat: answers.habitat,
      temperament: answers.temperament
    });
    var blanket_type = typeRow ? typeRow.blanket_type : null;
    var blanket_durability = typeRow ? typeRow.blanket_durability : null;
    trace.push({ step: "type", blanket_type: blanket_type, blanket_durability: blanket_durability });

    // 5. Weight rule -> 4 weight buckets
    var weightRow = findInTable(rules.weight, ["climate", "habitat", "horse_age", "coat", "body_condition"], {
      climate: climate,
      habitat: answers.habitat,
      horse_age: ageBucket(answers.horse_age),
      coat: answers.coat,
      body_condition: answers.body_condition
    });
    var weights = weightRow ? {
      stable_spring: weightRow.blanket_weight_stable_spring || "",
      stable_winter: weightRow.blanket_weight_stable_winter || "",
      turnout_spring: weightRow.blanket_weight_turnout_spring || "",
      turnout_winter: weightRow.blanket_weight_turnout_winter || ""
    } : { stable_spring: "", stable_winter: "", turnout_spring: "", turnout_winter: "" };
    trace.push({ step: "weight", weights: weights });

    // 6. Per-slot recommendation lookup
    var slots = {};
    var anyMatch = false;
    Object.keys(SLOT_TO_WEIGHT_COL).forEach(function (slot) {
      var weight = weights[slot];
      if (!weight) {
        slots[slot] = { slot: slot, weight: "", product_number: null, group: null, reason: "no_weight" };
        return;
      }
      var recWeightCol = SLOT_TO_WEIGHT_COL[slot];
      var recRow = null;
      for (var i = 0; i < rules.recommendations.length; i++) {
        var r = rules.recommendations[i];
        if (r.blanket_horse_type === blanket_horse_type &&
            r.horse_age === ageBucket(answers.horse_age) &&
            r.blanket_fit === blanket_fit &&
            r.blanket_style === blanket_style &&
            r.blanket_type === blanket_type &&
            r.blanket_durability === blanket_durability &&
            String(r[recWeightCol]) === String(weight) &&
            r.product) {
          recRow = r; break;
        }
      }
      if (recRow) anyMatch = true;
      slots[slot] = {
        slot: slot,
        weight: weight,
        product_number: recRow ? recRow.product : null,
        group: recRow ? recRow.product_group : null,
        reason: recRow ? "matched" : "no_recommendation"
      };
    });
    trace.push({ step: "recommendation", any_match: anyMatch });

    return {
      ok: anyMatch,
      derived: {
        state: state,
        climate: climate,
        blanket_horse_type: blanket_horse_type,
        blanket_size: blanket_size,
        blanket_fit: blanket_fit,
        blanket_style: blanket_style,
        blanket_type: blanket_type,
        blanket_durability: blanket_durability,
        weights: weights
      },
      slots: slots,
      trace: trace
    };
  }

  // --------------------------------------------------------------------------
  // Resolve product slot through handle map with group-based fallback
  // --------------------------------------------------------------------------
  function resolveSlot(slot, productMap, productGroups, opts, blanketSize) {
    if (!slot || !slot.product_number) return null;
    var primary = String(slot.product_number);
    var candidates = [primary];
    if (slot.group && productGroups && productGroups[slot.group]) {
      productGroups[slot.group].forEach(function (id) {
        if (id == null) return;
        var s = String(id);
        if (candidates.indexOf(s) === -1) candidates.push(s);
      });
    }
    var products = (productMap && productMap.products) || {};
    // Build /products/<handle>?variant=<id> using the SIZE-indexed
    // variant map from the handle map (Michelle 2026-08-20 8:40 PM
    // followup). The pre-8/20 build stored one variant_id per product;
    // that variant was whichever size sat at option index 0 (typically
    // 68), so every recommendation opened on size 68 regardless of
    // what the resolver picked. Now we look up
    // `entry.variants[blanketSize]` and fall through to no ?variant=
    // when the size isn't in the map — landing on the PDP default is
    // better than pre-selecting a known-wrong size (returns risk).
    var sizeKey = String(blanketSize == null ? "" : blanketSize);
    function urlFor(entry) {
      var u = opts.productUrlBase + entry.handle;
      var vid = (entry.variants && sizeKey && entry.variants[sizeKey]) || null;
      if (vid) {
        u += (u.indexOf("?") === -1 ? "?" : "&") + "variant=" + encodeURIComponent(vid);
      }
      return u;
    }
    for (var i = 0; i < candidates.length; i++) {
      var pid = candidates[i];
      var entry = products[pid];
      if (entry && entry.available !== false && entry.handle) {
        return {
          product_number: pid,
          handle: entry.handle,
          name: entry.name || null,
          image: entry.image || null,
          url: urlFor(entry),
          source: i === 0 ? "mapped" : "mapped-fallback"
        };
      }
    }
    for (var j = 0; j < candidates.length; j++) {
      var pid2 = candidates[j];
      var entry2 = products[pid2];
      if (entry2 && entry2.handle) {
        return {
          product_number: pid2,
          handle: entry2.handle,
          name: entry2.name || null,
          image: entry2.image || null,
          url: urlFor(entry2),
          source: "mapped-out-of-stock"
        };
      }
    }
    return {
      product_number: primary,
      handle: null, name: null, image: null,
      url: opts.fallbackSearchUrl + encodeURIComponent(primary),
      source: "search-fallback"
    };
  }

  // --------------------------------------------------------------------------
  // DOM helper
  // --------------------------------------------------------------------------
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === "class") node.className = attrs[k];
        else if (k === "dataset") Object.assign(node.dataset, attrs[k]);
        else if (k.indexOf("on") === 0 && typeof attrs[k] === "function") node.addEventListener(k.slice(2), attrs[k]);
        else if (k === "html") node.innerHTML = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) {
      if (c == null) return;
      if (typeof c === "string") node.appendChild(document.createTextNode(c));
      else node.appendChild(c);
    });
    return node;
  }

  // --------------------------------------------------------------------------
  // Complete event payload — used by GA4 + Klaviyo. Pure function.
  // --------------------------------------------------------------------------
  function buildCompletePayload(answers, resolved, slots, opts) {
    var derived = resolved.derived || {};
    var fit = fitCopy(derived.blanket_fit);
    var derivedNarrative = {
      horse_type: narrate("horse_type", derived.blanket_horse_type),
      fit: derived.blanket_fit || "",
      // fit_short + fit_lead added 2026-08-20 per fit-language guide, so
      // Michelle's Klaviyo template can render on-brand fit copy instead
      // of the raw resolver value. Breed-free per merch constraint.
      fit_short: fit ? fit.short : (derived.blanket_fit || ""),
      fit_lead: fit ? fit.lead : "",
      style: derived.blanket_style || "",
      type: derived.blanket_type || "",
      durability: derived.blanket_durability || "",
      size: derived.blanket_size || "",
      climate: narrate("climate", derived.climate),
      temperament: narrate("temperament", answers.temperament),
      habitat: narrate("habitat", answers.habitat),
      coat: narrate("coat", answers.coat),
      body_condition: narrate("body_condition", answers.body_condition)
    };

    var recommendedProducts = (slots || []).map(function (s) {
      var card = s.card || {};
      var slotDef = s.def || {};
      return {
        slot: slotDef.key,
        slot_label: SLOT_LABELS[slotDef.key] || slotDef.title,
        weight: s.weight || null,
        product_number: card.product_number,
        product_title: card.name || ("Product #" + card.product_number),
        product_url: card.url,
        product_image_url: card.image || null,
        handle: card.handle || null,
        source: card.source
      };
    });

    var horseSummarySentence = buildHorseSummary(answers, derived);

    var payload = Object.assign({}, derived, {
      horse_name: answers.horse_name || null,
      horse_summary_sentence: horseSummarySentence,
      // share_url added to Completed 2026-08-20 (Michelle handoff #6) so
      // the recap email can link back to the shopper's own result page
      // even on Completed events that skipped the email-capture step.
      share_url: opts ? (buildShareUrl(opts, answers) || null) : null,
      narrative: derivedNarrative,
      slots: recommendedProducts.map(function (p) {
        return { slot: p.slot, weight: p.weight, product_number: p.product_number, handle: p.handle, source: p.source };
      }),
      recommended_products: recommendedProducts
    });

    return { payload: payload, narrative: derivedNarrative, recommended_products: recommendedProducts };
  }

  // --------------------------------------------------------------------------
  // BlanketFitFinder controller
  // --------------------------------------------------------------------------
  function BlanketFitFinder(rootEl, opts) {
    this.root = rootEl;
    this.opts = Object.assign({}, DEFAULTS, opts || {});
    this.state = {
      stepIndex: 0,
      answers: {},
      started: false,
      completed: false,
      mode: "wizard", // wizard | profile-picker | result | shared | profile-review
      savedSnapshot: null
    };
    this.rules = null;
    this.productMap = null;
    this.abandonHandlerInstalled = false;
  }

  BlanketFitFinder.prototype.activeSteps = function () {
    // The email-capture step only participates in the flow when Klaviyo is
    // configured and the feature toggle is on. Filter it out otherwise so
    // step counters + navigation stay consistent.
    var self = this;
    return STEPS.filter(function (s) {
      if (s.type !== "email-capture") return true;
      return self.opts.enableEmailCapture && self.opts.klaviyoPublicKey;
    });
  };

  BlanketFitFinder.prototype.totalSteps = function () { return this.activeSteps().length; };

  BlanketFitFinder.prototype.heightOptions = function () {
    var t = this.state.answers.horse_type;
    var seen = {}, out = [];
    (this.rules && this.rules.size || []).forEach(function (r) {
      if (t && r.blanket_horse_type !== t) return;
      if (r.height && !seen[r.height]) { seen[r.height] = true; out.push(r.height); }
    });
    out.sort(function (a, b) {
      var na = parseFloat(a), nb = parseFloat(b);
      if (isNaN(na) || isNaN(nb)) return String(a).localeCompare(String(b));
      return na - nb;
    });
    return out;
  };

  BlanketFitFinder.prototype.breedOptions = function () {
    var t = this.state.answers.horse_type;
    var seen = {}, out = [];
    (this.rules && this.rules.size || []).forEach(function (r) {
      if (t && r.blanket_horse_type !== t) return;
      if (r.breed && !seen[r.breed]) { seen[r.breed] = true; out.push(r.breed); }
    });
    out.sort();
    return out;
  };

  BlanketFitFinder.prototype.installAbandonHandler = function () {
    if (this.abandonHandlerInstalled || !this.opts.enableAbandonTracking) return;
    var self = this;
    var fired = false;
    var handler = function () {
      if (fired) return;
      if (!self.state.started || self.state.completed) return;
      fired = true;
      var steps = self.activeSteps();
      var step = steps[self.state.stepIndex] || {};
      emit("blanket_finder_abandon", {
        last_step_index: self.state.stepIndex,
        last_step_id: step.id || null,
        answered: Object.keys(self.state.answers).length
      });
      if (self.state.answers.email) {
        klaviyoTrack(self.opts, "Blanket Finder Abandoned", {
          last_step_id: step.id || null,
          last_step_index: self.state.stepIndex,
          horse_name: self.state.answers.horse_name || null
        });
      }
    };
    if (typeof root.addEventListener === "function") {
      root.addEventListener("pagehide", handler);
      root.addEventListener("beforeunload", handler);
    }
    this.abandonHandlerInstalled = true;
  };

  BlanketFitFinder.prototype.mount = function () {
    var self = this;
    this.root.classList.add("bfinder");
    this.renderLoading();

    if (!this.root.dataset.bfinderKeyHandlerInstalled) {
      this.root.addEventListener("keydown", function (e) {
        if (e.key === "Escape") {
          if (self.state.mode === "wizard" && self.state.stepIndex > 0) {
            e.preventDefault();
            self.prev();
          }
        }
      });
      this.root.dataset.bfinderKeyHandlerInstalled = "1";
    }

    if (this.opts.customerId == null && root.Shopify && root.Shopify.customer && root.Shopify.customer.id) {
      this.opts.customerId = String(root.Shopify.customer.id);
    }

    var loaders = [fetchJSON(this.opts.rulesUrl)];
    if (this.opts.productMapUrl) loaders.push(fetchJSON(this.opts.productMapUrl).catch(function () { return null; }));

    Promise.all(loaders)
      .then(function (results) {
        var raw = results[0];
        self.rules = {
          size:            expandColumnar(raw.size_rules),
          climate:         expandColumnar(raw.climate_rules),
          fit:             expandColumnar(raw.fit_rules),
          type:            expandColumnar(raw.type_rules),
          weight:          expandColumnar(raw.weight_rules),
          recommendations: expandColumnar(raw.recommendations),
          product_groups:  raw.product_groups || {}
        };
        self.productMap = results[1] || null;
        self.installAbandonHandler();
        self.routeInitialView();
      })
      .catch(function (err) {
        self.renderError(err);
      });
  };

  BlanketFitFinder.prototype.routeInitialView = function () {
    var token = readShareTokenFromLocation();
    if (token) {
      var shared = decodeAnswers(token);
      if (shared && typeof shared === "object") {
        this.state.answers = shared;
        this.state.stepIndex = this.activeSteps().length - 1;
        this.state.started = true;
        this.state.completed = true;
        this.state.mode = "shared";
        emit("blanket_finder_share_open", { fields: Object.keys(shared) });
        this.renderResult();
        return;
      }
    }
    var profiles = loadProfiles(this.opts);
    var session = loadSession(this.opts);
    if ((profiles && profiles.length) || (session && session.answers && Object.keys(session.answers).length > 0)) {
      this.state.mode = "profile-picker";
      this.renderProfilePicker(profiles, session);
      return;
    }
    this.renderStep();
  };

  BlanketFitFinder.prototype.renderLoading = function () {
    this.root.replaceChildren();
    this.root.appendChild(el("div", { class: "bfinder-loading" }, ["Loading the Blanket Fit Finder…"]));
  };

  BlanketFitFinder.prototype.renderError = function (err) {
    this.root.replaceChildren();
    this.root.appendChild(el("div", { class: "bfinder-error", role: "alert" }, [
      "We couldn't load the Blanket Fit Finder right now. ",
      el("a", { href: this.opts.collectionUrl }, ["Browse all blankets"]),
      "."
    ]));
    if (root.console) root.console.error("[BlanketFitFinder]", err);
  };

  BlanketFitFinder.prototype.fireStartIfNeeded = function () {
    if (this.state.started) return;
    this.state.started = true;
    emit("blanket_finder_start", {});
  };

  BlanketFitFinder.prototype.setAnswer = function (id, value) {
    this.fireStartIfNeeded();
    this.state.answers[id] = value;
    emit("blanket_finder_step", { step: this.state.stepIndex + 1, field: id, value: value });
    saveSession(this.opts, this.state);
  };

  BlanketFitFinder.prototype.canAdvance = function () {
    var step = this.activeSteps()[this.state.stepIndex];
    if (!step) return false;
    if (step.optional) return true;
    var v = this.state.answers[step.id];
    return v != null && v !== "";
  };

  BlanketFitFinder.prototype.next = function () {
    if (!this.canAdvance()) return;
    var steps = this.activeSteps();
    if (this.state.stepIndex >= steps.length - 1) {
      this.renderResult();
      return;
    }
    this.state.stepIndex += 1;
    saveSession(this.opts, this.state);
    this.renderStep();
  };

  BlanketFitFinder.prototype.prev = function () {
    if (this.state.stepIndex === 0) return;
    this.state.stepIndex -= 1;
    saveSession(this.opts, this.state);
    this.renderStep();
  };

  BlanketFitFinder.prototype.startFresh = function () {
    this.state.answers = {};
    this.state.stepIndex = 0;
    this.state.started = false;
    this.state.completed = false;
    this.state.mode = "wizard";
    this.state.savedSnapshot = null;
    clearSession(this.opts);
    this.renderStep();
  };

  BlanketFitFinder.prototype.viewSavedProfileResult = function (profile) {
    if (!profile || !profile.answers) return;
    var current = this.state.answers || {};
    var hasInProgress = this.state.started && Object.keys(current).length > 0 && !this.state.completed;
    this.state.savedSnapshot = hasInProgress
      ? { answers: Object.assign({}, current), stepIndex: this.state.stepIndex }
      : null;
    this.state.answers = Object.assign({}, profile.answers);
    this.state.stepIndex = this.activeSteps().length - 1;
    this.state.completed = true;
    this.state.mode = "profile-review";
    emit("blanket_finder_resume", { step: this.state.stepIndex, source: "profile-review" });
    this.renderResult();
  };

  BlanketFitFinder.prototype.resumeWith = function (answers, stepIndex) {
    var steps = this.activeSteps();
    this.state.answers = Object.assign({}, answers || {});
    this.state.stepIndex = Math.max(0, Math.min(steps.length - 1, stepIndex || 0));
    this.state.started = true;
    this.state.completed = false;
    this.state.mode = "wizard";
    this.state.savedSnapshot = null;
    saveSession(this.opts, this.state);
    emit("blanket_finder_resume", { step: this.state.stepIndex, source: "profile-picker" });
    this.renderStep();
  };

  BlanketFitFinder.prototype.renderProfilePicker = function (profiles, session) {
    var self = this;
    var steps = this.activeSteps();
    this.root.replaceChildren();
    var hasSession = session && session.answers && Object.keys(session.answers).length > 0;
    var sessionStep = hasSession ? Math.min(session.stepIndex || 0, steps.length - 1) : 0;
    var sessionHorse = hasSession ? (session.answers.horse_name || "your in-progress finder") : null;

    var card = el("div", { class: "bfinder-card bfinder-picker" }, [
      el("h2", { class: "bfinder-question" }, ["Welcome back."]),
      el("p", { class: "bfinder-help" }, ["Pick up where you left off, or start fresh for a new horse."])
    ]);

    if (hasSession) {
      card.appendChild(el("div", { class: "bfinder-picker__row" }, [
        el("div", { class: "bfinder-picker__label" }, [
          el("strong", {}, ["Continue " + sessionHorse + "'s finder"]),
          el("div", { class: "bfinder-picker__sub" }, ["Step " + (sessionStep + 1) + " of " + steps.length])
        ]),
        el("button", {
          type: "button",
          class: "bfinder-btn bfinder-btn--primary",
          onclick: function () { self.resumeWith(session.answers, session.stepIndex); }
        }, ["Continue"])
      ]));
    }

    (profiles || []).forEach(function (p) {
      card.appendChild(el("div", { class: "bfinder-picker__row" }, [
        el("div", { class: "bfinder-picker__label" }, [
          el("strong", {}, [p.name || "Saved horse"]),
          el("div", { class: "bfinder-picker__sub" }, ["Last updated " + new Date(p.savedAt || Date.now()).toLocaleDateString()])
        ]),
        el("div", { class: "bfinder-picker__actions" }, [
          el("button", {
            type: "button", class: "bfinder-btn bfinder-btn--ghost",
            onclick: function () { self.viewSavedProfileResult(p); }
          }, ["See recommendation"]),
          el("button", {
            type: "button", class: "bfinder-btn bfinder-btn--ghost",
            onclick: function () {
              if (root.confirm && !root.confirm("Delete saved profile for " + (p.name || "this horse") + "?")) return;
              deleteProfile(self.opts, p.id);
              self.routeInitialView();
            }
          }, ["Delete"])
        ])
      ]));
    });

    card.appendChild(el("div", { class: "bfinder-picker__footer" }, [
      el("button", {
        type: "button", class: "bfinder-btn bfinder-btn--ghost",
        onclick: function () { self.startFresh(); }
      }, ["Start a new horse"])
    ]));

    this.root.appendChild(card);
  };

  BlanketFitFinder.prototype.renderStep = function () {
    var steps = this.activeSteps();
    var step = steps[this.state.stepIndex];
    if (step && step.type === "email-capture") return this.renderEmailCaptureStep();

    var self = this;
    var answers = this.state.answers;
    this.root.replaceChildren();

    var pct = Math.round((this.state.stepIndex / (steps.length - 1)) * 100);
    this.root.appendChild(el("div", { class: "bfinder-progress", role: "progressbar", "aria-valuenow": String(pct), "aria-valuemin": "0", "aria-valuemax": "100" }, [
      el("div", { class: "bfinder-progress__bar", style: "width:" + pct + "%" })
    ]));

    var personalizedLabel = step.label
      .replace("your horse's", answers.horse_name ? (answers.horse_name + "'s") : "your horse's")
      .replace("your horse", answers.horse_name || "your horse");

    var card = el("div", { class: "bfinder-card", tabindex: "-1" }, [
      el("div", { class: "bfinder-step-meta" }, ["Step " + (this.state.stepIndex + 1) + " of " + steps.length]),
      el("h2", { class: "bfinder-question" }, [personalizedLabel]),
      step.help ? el("p", { class: "bfinder-help" }, [step.help]) : null
    ]);

    if (step.id === "breed" || step.id === "height") {
      var warning = breedTypeWarning(answers);
      if (warning) {
        card.appendChild(el("div", { class: "bfinder-warning", role: "note" }, [
          el("strong", {}, ["Quick check: "]),
          warning
        ]));
      }
    }

    var input = this.renderInput(step);
    card.appendChild(input);

    if (step.measure) {
      var details = el("details", { class: "bfinder-measure" });
      details.appendChild(el("summary", {}, ["How do I tell?"]));
      details.appendChild(el("p", { class: "bfinder-measure__body" }, [step.measure]));
      if (this.opts.measuringGuideUrl) {
        details.appendChild(el("p", { class: "bfinder-measure__body" }, [
          el("a", { href: this.opts.measuringGuideUrl, target: "_blank", rel: "noopener" }, ["See Schneiders full measuring guide →"])
        ]));
      }
      card.appendChild(details);
    }

    var nav = el("div", { class: "bfinder-nav" }, [
      this.state.stepIndex > 0 ? el("button", { type: "button", class: "bfinder-btn bfinder-btn--ghost", onclick: function () { self.prev(); } }, ["Back"]) : null,
      el("button", {
        type: "button",
        class: "bfinder-btn bfinder-btn--primary",
        onclick: function () { self.next(); },
        "aria-disabled": this.canAdvance() ? "false" : "true"
      }, [this.state.stepIndex === steps.length - 1 ? "See my recommendations" : "Next"])
    ]);
    card.appendChild(nav);

    this.root.appendChild(card);
    setTimeout(function () { try { card.focus(); } catch (e) {} }, 0);

    var nextStep = steps[this.state.stepIndex + 1];
    if (nextStep && nextStep.type === "image-radio") {
      preloadImages(this.opts, (nextStep.options || []).map(function (o) { return o.image; }).filter(Boolean));
    }
    var afterNext = steps[this.state.stepIndex + 2];
    if (afterNext && afterNext.type === "image-radio") {
      preloadImages(this.opts, (afterNext.options || []).map(function (o) { return o.image; }).filter(Boolean));
    }
  };

  BlanketFitFinder.prototype.renderEmailCaptureStep = function () {
    var self = this;
    var steps = this.activeSteps();
    var step = steps[this.state.stepIndex];
    var answers = this.state.answers;
    this.root.replaceChildren();

    var pct = Math.round((this.state.stepIndex / (steps.length - 1)) * 100);
    this.root.appendChild(el("div", { class: "bfinder-progress", role: "progressbar", "aria-valuenow": String(pct), "aria-valuemin": "0", "aria-valuemax": "100" }, [
      el("div", { class: "bfinder-progress__bar", style: "width:" + pct + "%" })
    ]));

    var resolved = resolve(answers, this.rules);
    var slots = resolved.ok ? RESULT_SLOTS.map(function (slotDef) {
      var slot = resolved.slots[slotDef.key];
      var card = resolveSlot(slot, self.productMap, self.rules.product_groups, self.opts, resolved.derived && resolved.derived.blanket_size);
      return { def: slotDef, card: card, weight: (slot && slot.weight) || null };
    }).filter(function (s) { return s.card; }) : [];
    var built = buildCompletePayload(answers, resolved, slots, self.opts);
    var completePayload = built.payload;

    var horseName = answers.horse_name && String(answers.horse_name).trim();
    var heading = horseName
      ? "Want a copy of " + horseName + "'s recommendations?"
      : step.label;

    var card = el("div", { class: "bfinder-card", tabindex: "-1" }, [
      el("div", { class: "bfinder-step-meta" }, ["Step " + (this.state.stepIndex + 1) + " of " + steps.length]),
      el("h2", { class: "bfinder-question" }, [heading]),
      step.help ? el("p", { class: "bfinder-help" }, [step.help]) : null
    ]);

    var status = el("div", { class: "bfinder-email__status", role: "status", "aria-live": "polite" });
    var primary;
    var input = el("input", {
      type: "email", class: "bfinder-input", id: "bfinder-email_capture", name: "email",
      placeholder: "you@example.com", autocomplete: "email", "aria-label": "Email address",
      value: answers.email || "",
      oninput: function (e) { answers.email = e.target.value; if (status.textContent) status.textContent = ""; },
      onkeydown: function (e) { if (e.key === "Enter") { e.preventDefault(); if (primary) primary.click(); } }
    });
    var checkbox = el("input", { type: "checkbox", checked: "checked" });
    var optin = el("label", { class: "bfinder-email__optin" }, [
      checkbox,
      el("span", {}, ["Also send me Schneiders seasonal blanketing tips."])
    ]);

    card.appendChild(el("div", { class: "bfinder-field" }, [input]));
    card.appendChild(optin);
    card.appendChild(status);

    primary = el("button", {
      type: "button", class: "bfinder-btn bfinder-btn--primary",
      onclick: function () {
        var email = String(input.value || "").trim();
        if (!email) {
          self.state.emailCapturedAtStep = false;
          emit("blanket_finder_step15_skip", { reason: "blank_submit" });
          self.renderResult();
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          status.textContent = "Please enter a valid email address.";
          input.focus();
          return;
        }
        var optedIn = !!checkbox.checked;
        answers.email = email;
        answers.marketing_optin = optedIn;
        self.state.emailCapturedAtStep = true;
        klaviyoIdentify(self.opts, {
          $email: email,
          horse_name: answers.horse_name || null,
          blanket_finder_last_size: completePayload.blanket_size || null,
          blanket_finder_last_fit: completePayload.blanket_fit || null,
          blanket_finder_last_type: completePayload.blanket_type || null,
          blanket_finder_marketing_optin: optedIn
        });
        klaviyoTrack(self.opts, "Blanket Finder Email Capture", Object.assign({
          email: email,
          marketing_optin: optedIn,
          share_url: buildShareUrl(self.opts, answers),
          capture_point: "step_final"
        }, completePayload));
        if (optedIn) klaviyoSubscribe(self.opts, email);
        emit("blanket_finder_email_submit", { marketing_optin: optedIn, capture_point: "step_final" });
        self.renderResult();
      }
    }, ["See my recommendations"]);

    var nav = el("div", { class: "bfinder-nav bfinder-nav--email" }, [
      this.state.stepIndex > 0 ? el("button", { type: "button", class: "bfinder-btn bfinder-btn--ghost", onclick: function () { self.prev(); } }, ["Back"]) : null,
      primary
    ]);
    card.appendChild(nav);

    var skip = el("button", {
      type: "button", class: "bfinder-skip-link",
      onclick: function () {
        self.state.emailCapturedAtStep = false;
        emit("blanket_finder_step15_skip", { reason: "skip_clicked" });
        self.renderResult();
      }
    }, ["No thanks, just show me the recommendations"]);
    card.appendChild(el("div", { class: "bfinder-skip" }, [skip]));

    this.root.appendChild(card);
    setTimeout(function () { try { card.focus(); } catch (e) {} }, 0);
  };

  BlanketFitFinder.prototype.renderInput = function (step) {
    var self = this;
    var answers = this.state.answers;

    if (step.type === "text" || step.type === "zip" || step.type === "number") {
      var existing = answers[step.id] || "";
      var syncNav = function () {
        var btn = self.root.querySelector(".bfinder-btn--primary");
        if (btn) btn.setAttribute("aria-disabled", self.canAdvance() ? "false" : "true");
      };
      var input = el("input", {
        type: step.type === "number" ? "number" : (step.type === "zip" ? "tel" : "text"),
        class: "bfinder-input",
        id: "bfinder-" + step.id, name: step.id,
        placeholder: step.placeholder || "",
        value: existing,
        autocomplete: step.type === "zip" ? "postal-code" : "off",
        oninput: function (e) { self.setAnswer(step.id, e.target.value); syncNav(); },
        onkeydown: function (e) { if (e.key === "Enter") { e.preventDefault(); self.next(); } }
      });
      if (step.type === "number") {
        if (step.min != null) input.min = String(step.min);
        if (step.max != null) input.max = String(step.max);
      }
      return el("div", { class: "bfinder-field" }, [input]);
    }

    if (step.type === "select") {
      var options;
      if (step.dynamic === "height_options") options = this.heightOptions();
      else if (step.id === "breed") options = this.breedOptions();
      else options = step.options || [];
      var syncSelectNav = function () {
        var btn = self.root.querySelector(".bfinder-btn--primary");
        if (btn) btn.setAttribute("aria-disabled", self.canAdvance() ? "false" : "true");
      };
      var sel = el("select", {
        class: "bfinder-select", id: "bfinder-" + step.id, name: step.id,
        onchange: function (e) { self.setAnswer(step.id, e.target.value); syncSelectNav(); },
        onkeydown: function (e) { if (e.key === "Enter" && self.canAdvance()) { e.preventDefault(); self.next(); } }
      });
      sel.appendChild(el("option", { value: "" }, ["— Select —"]));
      options.forEach(function (o) {
        var v = typeof o === "string" ? o : o.value;
        var l = typeof o === "string" ? o : o.label;
        var opt = el("option", { value: v }, [l]);
        if (String(answers[step.id]) === String(v)) opt.selected = true;
        sel.appendChild(opt);
      });
      return el("div", { class: "bfinder-field" }, [sel]);
    }

    if (step.type === "radio" || step.type === "image-radio") {
      var grid = el("div", { class: "bfinder-grid bfinder-grid--" + step.type, role: "radiogroup", "aria-label": step.label });
      var cards = [];
      step.options.forEach(function (opt, idx) {
        var checked = answers[step.id] === opt.value;
        var card = el("button", {
          type: "button",
          class: "bfinder-choice" + (checked ? " is-selected" : ""),
          role: "radio",
          "aria-checked": checked ? "true" : "false",
          tabindex: checked || (idx === 0 && answers[step.id] == null) ? "0" : "-1",
          onclick: function () { self.setAnswer(step.id, opt.value); self.renderStep(); },
          onkeydown: function (e) {
            if (e.key === "ArrowRight" || e.key === "ArrowDown") {
              e.preventDefault(); var nxt = cards[(idx + 1) % cards.length]; if (nxt) nxt.focus();
            } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
              e.preventDefault(); var prev = cards[(idx - 1 + cards.length) % cards.length]; if (prev) prev.focus();
            } else if (e.key === "Enter" || e.key === " ") {
              e.preventDefault(); self.setAnswer(step.id, opt.value); self.renderStep();
            }
          }
        }, [
          opt.image ? el("img", { class: "bfinder-choice__img", src: imageUrl(self.opts, opt.image), alt: opt.label, loading: "lazy" }) : null,
          el("span", { class: "bfinder-choice__label" }, [opt.label])
        ]);
        cards.push(card);
        grid.appendChild(card);
      });
      return grid;
    }

    return el("div", {}, ["(unsupported step type: " + step.type + ")"]);
  };

  BlanketFitFinder.prototype.renderResult = function () {
    var self = this;
    var answers = this.state.answers;
    var resolved = resolve(answers, this.rules);
    this.state.completed = true;

    var horseName = answers.horse_name || "your horse";
    this.root.replaceChildren();

    if (!resolved.ok) {
      emit("blanket_finder_no_match", { trace: resolved.trace, blanket_horse_type: resolved.derived.blanket_horse_type || null });
      this.root.appendChild(el("div", { class: "bfinder-card bfinder-result bfinder-result--no-match" }, [
        el("h2", { class: "bfinder-question" }, ["We couldn't find a perfect match for " + horseName + "."]),
        el("p", { class: "bfinder-help" }, [
          "Our fit team can help — in the meantime, ",
          el("a", { href: self.opts.collectionUrl }, ["browse the full blanket collection"]),
          "."
        ]),
        el("div", { class: "bfinder-result__actions" }, [
          el("button", { type: "button", class: "bfinder-btn bfinder-btn--ghost", onclick: function () { self.startFresh(); } }, ["Start over"]),
          self.opts.fitSpecialistUrl ? el("a", { class: "bfinder-btn bfinder-btn--primary", href: self.opts.fitSpecialistUrl }, ["Talk to a fit specialist"]) : null,
          el("a", { class: "bfinder-btn bfinder-btn--primary", href: self.opts.collectionUrl }, ["See all blankets"])
        ])
      ]));
      return;
    }

    var slots = RESULT_SLOTS.map(function (slotDef) {
      var slot = resolved.slots[slotDef.key];
      var card = resolveSlot(slot, self.productMap, self.rules.product_groups, self.opts, resolved.derived && resolved.derived.blanket_size);
      return { def: slotDef, card: card, weight: (slot && slot.weight) || null };
    }).filter(function (s) { return s.card; });

    var built = buildCompletePayload(answers, resolved, slots, self.opts);
    var derivedNarrative = built.narrative;
    var completePayload = built.payload;

    var isProfileReview = self.state.mode === "profile-review";

    if (!isProfileReview) {
      emit("blanket_finder_complete", completePayload);
      klaviyoTrack(self.opts, "Blanket Finder Completed", completePayload);

      if (answers.horse_name) {
        var nameKey = String(answers.horse_name).trim().toLowerCase();
        if (nameKey) {
          saveProfile(self.opts, {
            id: "name:" + nameKey,
            name: answers.horse_name,
            savedAt: Date.now(),
            answers: answers
          });
        }
      }
      clearSession(self.opts);
    }

    var derived = resolved.derived || {};
    var fit = fitCopy(derived.blanket_fit);

    // Result-page summary. Uses the fit-language guide's `lead` sentence
    // (breed-free per merch constraint) when the fit is one we have copy
    // for; falls back to a generic sentence for anything else. Sizing
    // + climate framing is added on the second line so shoppers see WHY
    // this fit was picked without losing the practical "size 80 / OH /
    // cold" details they need to buy.
    var leadNode;
    if (fit) {
      leadNode = el("p", { class: "bfinder-help" }, [fit.lead]);
    } else {
      leadNode = el("p", { class: "bfinder-help" }, [
        "Based on ", el("strong", {}, [horseName]), "'s conformation, we're recommending our ",
        el("strong", {}, [derivedNarrative.fit || "matching"]), " fit."
      ]);
    }

    var contextChildren = [
      "Sized ", el("strong", {}, [String(derivedNarrative.size || "matched")]),
      " for a ", el("strong", {}, [derivedNarrative.horse_type || "horse"]),
      " in a ", el("strong", {}, [derivedNarrative.climate || "your"]), " climate"
    ];
    if (derived.state) contextChildren.push(" (", el("strong", {}, [derived.state]), ")");
    contextChildren.push(". ");
    if (derivedNarrative.type || derivedNarrative.durability) {
      contextChildren.push(
        "We're pairing that with ",
        el("strong", {}, [derivedNarrative.type || "matched"]),
        " blankets in the ",
        el("strong", {}, [derivedNarrative.durability || "matched"]),
        " durability tier for a horse who is " + (derivedNarrative.temperament || "in this profile") +
        (derivedNarrative.habitat ? (", " + derivedNarrative.habitat) : "") + "."
      );
    }

    var summary = el("div", { class: "bfinder-result__summary" }, [
      el("h2", { class: "bfinder-question" }, ["Here's what we recommend for " + horseName + "."]),
      leadNode,
      el("p", { class: "bfinder-help" }, contextChildren)
    ]);

    var grid = el("div", { class: "bfinder-results" });
    slots.forEach(function (s) {
      var card = el("a", {
        class: "bfinder-product" + (s.def.primary ? " bfinder-product--primary" : ""),
        href: s.card.url,
        target: "_top",
        onclick: function () {
          emit("blanket_finder_outbound_click", {
            slot: s.def.key,
            product_number: s.card.product_number,
            handle: s.card.handle,
            source: s.card.source
          });
        }
      }, [
        s.card.image ? el("img", { class: "bfinder-product__img", src: s.card.image, alt: s.card.name || s.def.title, loading: "lazy" }) : null,
        el("div", { class: "bfinder-product__body" }, [
          el("div", { class: "bfinder-product__role" }, [s.def.title]),
          s.weight ? el("div", { class: "bfinder-product__weight" }, ["Weight: " + s.weight]) : null,
          el("div", { class: "bfinder-product__name" }, [s.card.name || ("Product #" + s.card.product_number)]),
          el("div", { class: "bfinder-product__cta" }, ["View product →"])
        ])
      ]);
      grid.appendChild(card);
    });

    var actionsRow = [
      el("button", { type: "button", class: "bfinder-btn bfinder-btn--ghost", onclick: function () { self.startFresh(); } }, ["Start over"]),
      el("a", { class: "bfinder-btn bfinder-btn--ghost", href: self.opts.collectionUrl }, ["Browse the full collection"])
    ];
    if (self.opts.fitSpecialistUrl) {
      actionsRow.push(el("a", { class: "bfinder-btn bfinder-btn--ghost", href: self.opts.fitSpecialistUrl }, ["Talk to a fit specialist"]));
    }
    var actions = el("div", { class: "bfinder-result__actions" }, actionsRow);

    var card = el("div", { class: "bfinder-card bfinder-result" }, []);

    if (isProfileReview && self.state.savedSnapshot) {
      var snapshot = self.state.savedSnapshot;
      var snapHorse = (snapshot.answers && snapshot.answers.horse_name) || "your in-progress finder";
      card.appendChild(el("div", { class: "bfinder-result__back" }, [
        el("button", {
          type: "button", class: "bfinder-btn bfinder-btn--ghost",
          onclick: function () {
            self.state.answers = Object.assign({}, snapshot.answers || {});
            self.state.stepIndex = Math.max(0, Math.min(self.activeSteps().length - 1, snapshot.stepIndex || 0));
            self.state.completed = false;
            self.state.mode = "wizard";
            self.state.savedSnapshot = null;
            saveSession(self.opts, self.state);
            self.renderStep();
          }
        }, ["← Back to " + snapHorse + "'s in-progress finder"])
      ]));
    }

    card.appendChild(summary);

    if (self.opts.enableShareLinks) {
      var shareUrl = buildShareUrl(self.opts, answers);
      if (shareUrl) {
        var shareInput = el("input", {
          type: "text", class: "bfinder-share__input", readonly: "readonly",
          value: shareUrl, "aria-label": "Shareable result URL"
        });
        var shareBtn = el("button", {
          type: "button", class: "bfinder-btn bfinder-btn--ghost",
          onclick: function () {
            shareInput.focus(); shareInput.select();
            var supported = false;
            try { supported = root.navigator && root.navigator.clipboard && typeof root.navigator.clipboard.writeText === "function"; } catch (e) {}
            if (supported) {
              root.navigator.clipboard.writeText(shareUrl).then(function () {
                shareBtn.textContent = "Copied!";
                setTimeout(function () { shareBtn.textContent = "Copy link"; }, 1800);
              }).catch(function () { document.execCommand && document.execCommand("copy"); });
            } else if (document.execCommand) {
              try { document.execCommand("copy"); shareBtn.textContent = "Copied!"; setTimeout(function () { shareBtn.textContent = "Copy link"; }, 1800); } catch (e) {}
            }
            emit("blanket_finder_share_copy", { fields: Object.keys(answers || {}) });
          }
        }, ["Copy link"]);
        card.appendChild(el("div", { class: "bfinder-share" }, [
          el("label", { class: "bfinder-share__label" }, ["Share this recommendation with your barn"]),
          el("div", { class: "bfinder-share__row" }, [shareInput, shareBtn])
        ]));
      }
    }

    if (!isProfileReview && self.opts.enableEmailCapture && self.opts.klaviyoPublicKey) {
      if (self.state.emailCapturedAtStep) {
        card.appendChild(this.renderEmailCaptureSent());
      } else {
        card.appendChild(this.renderEmailCapture(completePayload));
      }
    }

    // Turnout-only explainer: when the resolved blanket_type is `Turnout`,
    // the merch rules only recommend Turnout-season blankets — one blanket
    // does both stable + turnout duty. Without this note the shopper sees
    // just two cards and wonders why we skipped the stable slots.
    if ((derived.blanket_type || "") === "Turnout") {
      var isFullTurnout = answers.habitat === "24-7 Turnout with Run-in shed";
      var explainerText = isFullTurnout
        ? "Because your horse is turned out around the clock, we recommend one versatile Turnout blanket for each season. It handles the shed and the open field — no separate stable blanket needed."
        : "Because your horse is turned out in all weather, we recommend one versatile Turnout blanket for each season. It works both inside and out — no separate stable blanket needed.";
      card.appendChild(el("div", { class: "bfinder-explainer", role: "note" }, [explainerText]));
    }

    // "About the [Fit] fit" expandable — one block per result page (all
    // cards on the page share the same resolved fit). Uses the .bfinder-
    // measure details/summary pattern for consistent styling. Populated
    // from FIT_COPY (breed-free per merch constraint).
    if (fit) {
      var fitDetails = el("details", { class: "bfinder-measure bfinder-fit-about" });
      fitDetails.appendChild(el("summary", {}, ["About the " + fit.short + " fit"]));
      fitDetails.appendChild(el("p", { class: "bfinder-measure__body" }, [fit.lead]));
      var ul = el("ul", { class: "bfinder-fit-about__bullets" });
      (fit.bullets || []).forEach(function (b) { ul.appendChild(el("li", {}, [b])); });
      fitDetails.appendChild(ul);
      card.appendChild(fitDetails);
    }

    card.appendChild(grid);
    card.appendChild(actions);

    if (self.opts.eqLearningUrl || self.opts.eqMainUrl) {
      var eq = el("div", { class: "bfinder-results__eq" });
      eq.appendChild(el("h3", { class: "bfinder-results__eq-heading" }, ["Join the Schneiders EQ community"]));
      var eqCtas = el("div", { class: "bfinder-results__eq-ctas" });
      if (self.opts.eqLearningUrl) {
        eqCtas.appendChild(el("a", {
          href: self.opts.eqLearningUrl, target: "_blank", rel: "noopener",
          class: "bfinder-btn bfinder-btn--primary"
        }, ["Learn more about blanketing"]));
      }
      if (self.opts.eqMainUrl) {
        eqCtas.appendChild(el("a", {
          href: self.opts.eqMainUrl, target: "_blank", rel: "noopener",
          class: "bfinder-btn bfinder-btn--ghost"
        }, ["Explore the EQ community"]));
      }
      eq.appendChild(eqCtas);
      card.appendChild(eq);
    }

    this.root.appendChild(card);

    if (typeof this.opts.onComplete === "function") {
      try { this.opts.onComplete(resolved); } catch (e) {}
    }
  };

  BlanketFitFinder.prototype.renderEmailCaptureSent = function () {
    return el("div", { class: "bfinder-email bfinder-email--confirm" }, [
      el("h3", { class: "bfinder-email__heading" }, ["✓ Sent — check your inbox."]),
      el("p", { class: "bfinder-email__help" }, [
        "We just emailed your personalized blanket recommendations. The link in that email opens this exact page so you can come back anytime."
      ])
    ]);
  };

  BlanketFitFinder.prototype.renderEmailCapture = function (completePayload) {
    var self = this;
    var wrap = el("div", { class: "bfinder-email" });
    var heading = el("h3", { class: "bfinder-email__heading" }, ["Email me these recommendations"]);
    var help = el("p", { class: "bfinder-email__help" }, ["We'll send a link to this exact result so you can review it later or forward it to your barn."]);
    var input = el("input", {
      type: "email", class: "bfinder-input", placeholder: "you@example.com",
      autocomplete: "email", "aria-label": "Email address"
    });
    var optin = el("label", { class: "bfinder-email__optin" }, [
      el("input", { type: "checkbox", checked: "checked" }),
      el("span", {}, ["Also send me Schneiders seasonal blanketing tips."])
    ]);
    var status = el("div", { class: "bfinder-email__status", role: "status", "aria-live": "polite" });
    var submit = el("button", {
      type: "button", class: "bfinder-btn bfinder-btn--primary",
      onclick: function () {
        var email = String(input.value || "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          status.textContent = "Please enter a valid email address.";
          return;
        }
        var optedIn = optin.querySelector("input[type=checkbox]").checked;
        self.state.answers.email = email;
        klaviyoIdentify(self.opts, {
          $email: email,
          horse_name: self.state.answers.horse_name || null,
          blanket_finder_last_size: completePayload.blanket_size || null,
          blanket_finder_last_fit: completePayload.blanket_fit || null,
          blanket_finder_last_type: completePayload.blanket_type || null,
          blanket_finder_marketing_optin: !!optedIn
        });
        klaviyoTrack(self.opts, "Blanket Finder Email Capture", Object.assign({
          email: email,
          marketing_optin: !!optedIn,
          share_url: buildShareUrl(self.opts, self.state.answers)
        }, completePayload));
        if (optedIn) klaviyoSubscribe(self.opts, email);
        emit("blanket_finder_email_submit", { marketing_optin: !!optedIn });
        status.textContent = "Sent! Check your inbox in a minute or two.";
        submit.disabled = true;
        input.disabled = true;
      }
    }, ["Email me my results"]);
    wrap.appendChild(heading);
    wrap.appendChild(help);
    wrap.appendChild(el("div", { class: "bfinder-email__row" }, [input, submit]));
    wrap.appendChild(optin);
    wrap.appendChild(status);
    return wrap;
  };

  // --------------------------------------------------------------------------
  // Public surface
  // --------------------------------------------------------------------------
  root.BlanketFitFinder = {
    mount: function (rootEl, opts) {
      var instance = new BlanketFitFinder(rootEl, opts);
      instance.mount();
      return instance;
    },
    resolve: function (answers, rules) { return resolve(answers, rules); },
    expandColumnar: expandColumnar,
    STEPS: STEPS,
    RESULT_SLOTS: RESULT_SLOTS,
    NARRATIVE: NARRATIVE,
    narrate: narrate,
    ageNarrative: ageNarrative,
    buildHorseSummary: buildHorseSummary,
    buildCompletePayload: buildCompletePayload,
    resolveSlot: resolveSlot,
    breedTypeWarning: breedTypeWarning,
    encodeAnswers: encodeAnswers,
    decodeAnswers: decodeAnswers,
    buildShareUrl: function (opts, answers) { return buildShareUrl(Object.assign({}, DEFAULTS, opts || {}), answers); },
    klaviyoSubscribe: klaviyoSubscribe,
    findClosestSizeRow: findClosestSizeRow
  };
})(typeof window !== "undefined" ? window : globalThis);
